import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { brokers, submissions, accounts } from '../services/api.js';
import { useAuth } from '../auth/AuthContext.jsx';
import './BrokerDetail.css';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

const TABS = ['Overview', 'Contacts', 'Activity', 'Submissions & Accounts'];

const ACTIVITY_TYPES = ['Meeting', 'Call', 'Email', 'Note'];

const ACTIVITY_ICONS = {
  Meeting: '\u{1F91D}',
  Call: '\u{1F4DE}',
  Email: '\u{1F4E7}',
  Note: '\u{1F4DD}',
  System: '\u{2699}\u{FE0F}',
};

export function BrokerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [broker, setBroker] = useState(null);
  const [allBrokers, setAllBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('Overview');

  // Edit broker state
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  // Contacts state (local)
  const [contacts, setContacts] = useState([]);
  const [showAddContact, setShowAddContact] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    title: '',
    primary: false,
  });

  // Activity state (local)
  const [activities, setActivities] = useState([]);
  const [showLogActivity, setShowLogActivity] = useState(false);
  const [activityForm, setActivityForm] = useState({
    type: 'Note',
    subject: '',
    description: '',
  });

  // Submissions & Accounts state
  const [brokerSubmissions, setBrokerSubmissions] = useState([]);
  const [brokerAccounts, setBrokerAccounts] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsError, setSubsError] = useState(null);
  const [acctsError, setAcctsError] = useState(null);

  // Sub-agents: add office form
  const [showAddOffice, setShowAddOffice] = useState(false);
  const [officeForm, setOfficeForm] = useState({
    Name: '',
    SubCode: '',
    City: '',
    State: '',
    ContactEmail: '',
  });
  const [officeSaving, setOfficeSaving] = useState(false);

  const loadBroker = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [brokerRes, listRes] = await Promise.allSettled([
        brokers.get(id),
        brokers.list(),
      ]);

      if (brokerRes.status === 'fulfilled') {
        const data = brokerRes.value.data || brokerRes.value;
        setBroker(data);

        // Initialize default contact from broker email
        if (data.ContactEmail) {
          setContacts([
            {
              id: 'default',
              name: data.Name || 'Primary Contact',
              email: data.ContactEmail,
              phone: data.Phone || '',
              title: 'Primary Contact',
              primary: true,
            },
          ]);
        }
      } else {
        setError(brokerRes.reason?.message || 'Failed to load broker');
      }

      if (listRes.status === 'fulfilled') {
        const list = listRes.value.data || listRes.value;
        setAllBrokers(Array.isArray(list) ? list : []);
      }

      // Seed demo activities
      setActivities([
        {
          id: 'sys-1',
          type: 'System',
          subject: 'Broker account created',
          description: 'Broker was onboarded into the DEX platform.',
          date: new Date().toISOString(),
          user: 'System',
        },
        {
          id: 'sys-2',
          type: 'System',
          subject: 'First submission received',
          description:
            'The first submission was received from this broker.',
          date: new Date(Date.now() - 86400000).toISOString(),
          user: 'System',
        },
      ]);
    } catch (err) {
      setError(err.message || 'Failed to load broker');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadBroker();
  }, [loadBroker]);

  // Load submissions & accounts when tab is activated
  useEffect(() => {
    if (activeTab !== 'Submissions & Accounts') return;
    let cancelled = false;

    async function loadSubsAccts() {
      setSubsLoading(true);
      setSubsError(null);
      setAcctsError(null);

      const [subsRes, acctsRes] = await Promise.allSettled([
        submissions.list({ brokerId: id }),
        accounts.list({ brokerId: id }),
      ]);

      if (cancelled) return;

      if (subsRes.status === 'fulfilled') {
        const data = subsRes.value.data || subsRes.value;
        setBrokerSubmissions(Array.isArray(data) ? data : []);
      } else {
        setSubsError(subsRes.reason?.message || 'Could not load submissions');
      }

      if (acctsRes.status === 'fulfilled') {
        const data = acctsRes.value.data || acctsRes.value;
        setBrokerAccounts(Array.isArray(data) ? data : []);
      } else {
        setAcctsError(acctsRes.reason?.message || 'Could not load accounts');
      }

      setSubsLoading(false);
    }

    loadSubsAccts();
    return () => {
      cancelled = true;
    };
  }, [activeTab, id]);

  // Edit handlers
  const startEditing = () => {
    setEditData({ ...broker });
    setEditing(true);
    setEditError(null);
  };

  const handleEditChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const saveEdit = async () => {
    setEditSaving(true);
    setEditError(null);
    try {
      await brokers.update(id, editData);
      setBroker(editData);
      setEditing(false);
    } catch (err) {
      setEditError(err.message || 'Failed to update broker');
    } finally {
      setEditSaving(false);
    }
  };

  // Contact handlers
  const handleAddContact = (e) => {
    e.preventDefault();
    const newContact = {
      id: `c-${Date.now()}`,
      ...contactForm,
    };
    if (contactForm.primary) {
      setContacts((prev) =>
        prev.map((c) => ({ ...c, primary: false })).concat(newContact)
      );
    } else {
      setContacts((prev) => [...prev, newContact]);
    }
    setContactForm({ name: '', email: '', phone: '', title: '', primary: false });
    setShowAddContact(false);
  };

  // Activity handlers
  const handleLogActivity = (e) => {
    e.preventDefault();
    const newActivity = {
      id: `a-${Date.now()}`,
      type: activityForm.type,
      subject: activityForm.subject,
      description: activityForm.description,
      date: new Date().toISOString(),
      user: user?.displayName || user?.username || 'Current User',
    };
    setActivities((prev) => [newActivity, ...prev]);
    setActivityForm({ type: 'Note', subject: '', description: '' });
    setShowLogActivity(false);
  };

  // Add office handler
  const handleAddOffice = async (e) => {
    e.preventDefault();
    setOfficeSaving(true);
    try {
      await brokers.create({
        ...officeForm,
        Type: broker?.Type || 'wholesale',
        ParentBrokerID: id,
        Active: true,
      });
      setShowAddOffice(false);
      setOfficeForm({ Name: '', SubCode: '', City: '', State: '', ContactEmail: '' });
      // Refresh broker list for sub-agents
      const listRes = await brokers.list();
      const list = listRes.data || listRes;
      setAllBrokers(Array.isArray(list) ? list : []);
    } catch {
      // Silently handle - the add office is best-effort
    } finally {
      setOfficeSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bkd-loading">
        <div className="spinner" />
        <p>Loading broker...</p>
      </div>
    );
  }

  if (error && !broker) {
    return (
      <div className="bkd-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button className="btn btn--primary" onClick={loadBroker}>
          Retry
        </button>
      </div>
    );
  }

  if (!broker) return null;

  const subAgents = allBrokers.filter(
    (b) => b.ParentBrokerID === broker.BrokerID && b.BrokerID !== broker.BrokerID
  );

  return (
    <div className="bkd">
      {/* Back link */}
      <div className="bkd__breadcrumb">
        <button className="bkd__back" onClick={() => navigate('/brokers')}>
          &larr; Back to Brokers
        </button>
      </div>

      {/* Header */}
      <div className="bkd__header card">
        <div className="bkd__header-top">
          <div className="bkd__header-info">
            <h1 className="bkd__name">{broker.Name || 'Unknown Broker'}</h1>
            <div className="bkd__header-badges">
              <span
                className={`bkd__type-badge bkd__type-badge--${broker.Type || 'wholesale'}`}
              >
                {broker.Type || '—'}
              </span>
              {broker.MasterCode && (
                <span className="bkd__code-badge">{broker.MasterCode}</span>
              )}
              <span
                className={`bkd__active-badge ${broker.Active !== false ? 'bkd__active-badge--active' : 'bkd__active-badge--inactive'}`}
              >
                {broker.Active !== false ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
          {!editing && (
            <button className="btn btn--secondary btn--sm" onClick={startEditing}>
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Edit form */}
      {editing && (
        <div className="bkd__edit-card card">
          <h3 className="bkd__edit-title">Edit Broker Information</h3>
          <div className="bkd__edit-grid">
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">Name</label>
              <input
                className="bkd__edit-input"
                value={editData.Name || ''}
                onChange={(e) => handleEditChange('Name', e.target.value)}
              />
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">Type</label>
              <select
                className="bkd__edit-input"
                value={editData.Type || 'wholesale'}
                onChange={(e) => handleEditChange('Type', e.target.value)}
              >
                <option value="wholesale">Wholesale</option>
                <option value="retail">Retail</option>
              </select>
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">Contact Email</label>
              <input
                className="bkd__edit-input"
                type="email"
                value={editData.ContactEmail || ''}
                onChange={(e) => handleEditChange('ContactEmail', e.target.value)}
              />
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">Master Code</label>
              <input
                className="bkd__edit-input"
                value={editData.MasterCode || ''}
                onChange={(e) => handleEditChange('MasterCode', e.target.value)}
              />
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">Phone</label>
              <input
                className="bkd__edit-input"
                value={editData.Phone || ''}
                onChange={(e) => handleEditChange('Phone', e.target.value)}
              />
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">Website</label>
              <input
                className="bkd__edit-input"
                value={editData.Website || ''}
                onChange={(e) => handleEditChange('Website', e.target.value)}
              />
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">Address</label>
              <input
                className="bkd__edit-input"
                value={editData.Address || ''}
                onChange={(e) => handleEditChange('Address', e.target.value)}
              />
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">City</label>
              <input
                className="bkd__edit-input"
                value={editData.City || ''}
                onChange={(e) => handleEditChange('City', e.target.value)}
              />
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">State</label>
              <input
                className="bkd__edit-input"
                maxLength={2}
                value={editData.State || ''}
                onChange={(e) =>
                  handleEditChange('State', e.target.value.toUpperCase())
                }
              />
            </div>
            <div className="bkd__edit-field">
              <label className="bkd__edit-label">Zip Code</label>
              <input
                className="bkd__edit-input"
                value={editData.ZipCode || ''}
                onChange={(e) => handleEditChange('ZipCode', e.target.value)}
              />
            </div>
            <div className="bkd__edit-field bkd__edit-field--full">
              <label className="bkd__edit-label">Notes</label>
              <textarea
                className="bkd__edit-input bkd__edit-textarea"
                value={editData.Notes || ''}
                onChange={(e) => handleEditChange('Notes', e.target.value)}
                rows={3}
              />
            </div>
          </div>
          {editError && <p className="bkd__edit-error">{editError}</p>}
          <div className="bkd__edit-actions">
            <button
              className="btn btn--secondary"
              onClick={() => setEditing(false)}
              disabled={editSaving}
            >
              Cancel
            </button>
            <button
              className="btn btn--primary"
              onClick={saveEdit}
              disabled={editSaving}
            >
              {editSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bkd__tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`bkd__tab ${activeTab === tab ? 'bkd__tab--active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bkd__content">
        {/* ========= OVERVIEW ========= */}
        {activeTab === 'Overview' && (
          <div className="bkd__overview">
            {/* Company Info */}
            <div className="bkd__info-card card">
              <h3 className="bkd__section-title">Company Information</h3>
              <dl className="bkd__info-grid">
                <div className="bkd__info-item">
                  <dt>Address</dt>
                  <dd>
                    {[broker.Address, broker.City, broker.State, broker.ZipCode]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </dd>
                </div>
                <div className="bkd__info-item">
                  <dt>Phone</dt>
                  <dd>{broker.Phone || '—'}</dd>
                </div>
                <div className="bkd__info-item">
                  <dt>Website</dt>
                  <dd>
                    {broker.Website ? (
                      <a
                        href={
                          broker.Website.startsWith('http')
                            ? broker.Website
                            : `https://${broker.Website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bkd__link"
                      >
                        {broker.Website}
                      </a>
                    ) : (
                      '—'
                    )}
                  </dd>
                </div>
                <div className="bkd__info-item">
                  <dt>Contact Email</dt>
                  <dd>{broker.ContactEmail || '—'}</dd>
                </div>
                <div className="bkd__info-item">
                  <dt>Master Code</dt>
                  <dd className="bkd__mono">{broker.MasterCode || '—'}</dd>
                </div>
                <div className="bkd__info-item">
                  <dt>Sub Code</dt>
                  <dd className="bkd__mono">{broker.SubCode || '—'}</dd>
                </div>
                <div className="bkd__info-item">
                  <dt>Created</dt>
                  <dd>{formatDate(broker.CreatedAt)}</dd>
                </div>
                <div className="bkd__info-item">
                  <dt>Last Updated</dt>
                  <dd>{formatDate(broker.UpdatedAt)}</dd>
                </div>
              </dl>
              {broker.Notes && (
                <div className="bkd__notes">
                  <dt>Notes</dt>
                  <dd>{broker.Notes}</dd>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bkd__stats-row">
              <div className="bkd__stat-card card">
                <span className="bkd__stat-label">Total Submissions</span>
                <span className="bkd__stat-value">24</span>
              </div>
              <div className="bkd__stat-card card">
                <span className="bkd__stat-label">Bound Policies</span>
                <span className="bkd__stat-value bkd__stat-value--green">8</span>
              </div>
              <div className="bkd__stat-card card">
                <span className="bkd__stat-label">Active Accounts</span>
                <span className="bkd__stat-value">12</span>
              </div>
            </div>

            {/* Sub-Agents / Offices */}
            <div className="bkd__sub-section card">
              <div className="bkd__sub-header">
                <h3 className="bkd__section-title">Offices / Teams</h3>
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={() => setShowAddOffice(true)}
                >
                  + Add Office
                </button>
              </div>

              {showAddOffice && (
                <form className="bkd__office-form" onSubmit={handleAddOffice}>
                  <div className="bkd__office-form-grid">
                    <input
                      className="bkd__edit-input"
                      placeholder="Office name"
                      required
                      value={officeForm.Name}
                      onChange={(e) =>
                        setOfficeForm((f) => ({ ...f, Name: e.target.value }))
                      }
                    />
                    <input
                      className="bkd__edit-input"
                      placeholder="Sub code"
                      value={officeForm.SubCode}
                      onChange={(e) =>
                        setOfficeForm((f) => ({ ...f, SubCode: e.target.value }))
                      }
                    />
                    <input
                      className="bkd__edit-input"
                      placeholder="City"
                      value={officeForm.City}
                      onChange={(e) =>
                        setOfficeForm((f) => ({ ...f, City: e.target.value }))
                      }
                    />
                    <input
                      className="bkd__edit-input"
                      placeholder="State"
                      maxLength={2}
                      value={officeForm.State}
                      onChange={(e) =>
                        setOfficeForm((f) => ({
                          ...f,
                          State: e.target.value.toUpperCase(),
                        }))
                      }
                    />
                    <input
                      className="bkd__edit-input"
                      placeholder="Contact email"
                      type="email"
                      value={officeForm.ContactEmail}
                      onChange={(e) =>
                        setOfficeForm((f) => ({
                          ...f,
                          ContactEmail: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="bkd__office-form-actions">
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => setShowAddOffice(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn--primary btn--sm"
                      disabled={officeSaving}
                    >
                      {officeSaving ? 'Adding...' : 'Add'}
                    </button>
                  </div>
                </form>
              )}

              {subAgents.length > 0 ? (
                <div className="bkd__sub-list">
                  {subAgents.map((sub) => (
                    <div
                      key={sub.BrokerID}
                      className="bkd__sub-item"
                      onClick={() => navigate(`/brokers/${sub.BrokerID}`)}
                    >
                      <div className="bkd__sub-name">{sub.Name}</div>
                      <div className="bkd__sub-meta">
                        {sub.SubCode && (
                          <span className="bkd__mono">{sub.SubCode}</span>
                        )}
                        <span>
                          {[sub.City, sub.State].filter(Boolean).join(', ') ||
                            '—'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="bkd__empty">No offices or teams found.</p>
              )}
            </div>
          </div>
        )}

        {/* ========= CONTACTS ========= */}
        {activeTab === 'Contacts' && (
          <div className="bkd__contacts">
            <div className="bkd__contacts-header">
              <h3 className="bkd__section-title">Contacts</h3>
              <button
                className="btn btn--secondary btn--sm"
                onClick={() => setShowAddContact(true)}
              >
                + Add Contact
              </button>
            </div>

            {showAddContact && (
              <form
                className="bkd__contact-form card"
                onSubmit={handleAddContact}
              >
                <div className="bkd__contact-form-grid">
                  <input
                    className="bkd__edit-input"
                    placeholder="Full name"
                    required
                    value={contactForm.name}
                    onChange={(e) =>
                      setContactForm((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                  <input
                    className="bkd__edit-input"
                    placeholder="Email"
                    type="email"
                    value={contactForm.email}
                    onChange={(e) =>
                      setContactForm((f) => ({ ...f, email: e.target.value }))
                    }
                  />
                  <input
                    className="bkd__edit-input"
                    placeholder="Phone"
                    value={contactForm.phone}
                    onChange={(e) =>
                      setContactForm((f) => ({ ...f, phone: e.target.value }))
                    }
                  />
                  <input
                    className="bkd__edit-input"
                    placeholder="Title / Role"
                    value={contactForm.title}
                    onChange={(e) =>
                      setContactForm((f) => ({ ...f, title: e.target.value }))
                    }
                  />
                  <label className="bkd__checkbox-label">
                    <input
                      type="checkbox"
                      checked={contactForm.primary}
                      onChange={(e) =>
                        setContactForm((f) => ({
                          ...f,
                          primary: e.target.checked,
                        }))
                      }
                    />
                    Primary contact
                  </label>
                </div>
                <div className="bkd__contact-form-actions">
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={() => setShowAddContact(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn--primary btn--sm">
                    Add Contact
                  </button>
                </div>
              </form>
            )}

            {contacts.length > 0 ? (
              <div className="bkd__contact-list">
                {contacts.map((c) => (
                  <div key={c.id} className="bkd__contact-item card">
                    <div className="bkd__contact-top">
                      <span className="bkd__contact-name">{c.name}</span>
                      {c.primary && (
                        <span className="bkd__primary-badge">Primary</span>
                      )}
                    </div>
                    {c.title && (
                      <span className="bkd__contact-title">{c.title}</span>
                    )}
                    <div className="bkd__contact-details">
                      {c.email && <span>{c.email}</span>}
                      {c.phone && <span>{c.phone}</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="bkd__empty">No contacts added yet.</p>
            )}
          </div>
        )}

        {/* ========= ACTIVITY ========= */}
        {activeTab === 'Activity' && (
          <div className="bkd__activity">
            <div className="bkd__activity-header">
              <h3 className="bkd__section-title">Activity Timeline</h3>
              <button
                className="btn btn--secondary btn--sm"
                onClick={() => setShowLogActivity(true)}
              >
                + Log Activity
              </button>
            </div>

            {showLogActivity && (
              <form
                className="bkd__activity-form card"
                onSubmit={handleLogActivity}
              >
                <div className="bkd__activity-form-grid">
                  <div className="bkd__edit-field">
                    <label className="bkd__edit-label">Type</label>
                    <select
                      className="bkd__edit-input"
                      value={activityForm.type}
                      onChange={(e) =>
                        setActivityForm((f) => ({
                          ...f,
                          type: e.target.value,
                        }))
                      }
                    >
                      {ACTIVITY_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="bkd__edit-field">
                    <label className="bkd__edit-label">Subject</label>
                    <input
                      className="bkd__edit-input"
                      required
                      value={activityForm.subject}
                      onChange={(e) =>
                        setActivityForm((f) => ({
                          ...f,
                          subject: e.target.value,
                        }))
                      }
                      placeholder="Brief subject"
                    />
                  </div>
                  <div className="bkd__edit-field bkd__edit-field--full">
                    <label className="bkd__edit-label">Description</label>
                    <textarea
                      className="bkd__edit-input bkd__edit-textarea"
                      value={activityForm.description}
                      onChange={(e) =>
                        setActivityForm((f) => ({
                          ...f,
                          description: e.target.value,
                        }))
                      }
                      rows={3}
                      placeholder="Details..."
                    />
                  </div>
                </div>
                <div className="bkd__activity-form-actions">
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={() => setShowLogActivity(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn--primary btn--sm">
                    Log Activity
                  </button>
                </div>
              </form>
            )}

            {activities.length > 0 ? (
              <div className="bkd__timeline">
                {activities.map((act, idx) => (
                  <div key={act.id} className="bkd__timeline-item">
                    <div className="bkd__timeline-marker">
                      <div className="bkd__timeline-icon">
                        {ACTIVITY_ICONS[act.type] || ACTIVITY_ICONS.Note}
                      </div>
                      {idx < activities.length - 1 && (
                        <div className="bkd__timeline-line" />
                      )}
                    </div>
                    <div className="bkd__timeline-content">
                      <div className="bkd__timeline-header">
                        <div>
                          <span className="bkd__timeline-type">{act.type}</span>
                          <strong className="bkd__timeline-subject">
                            {act.subject}
                          </strong>
                        </div>
                        <span className="bkd__timeline-date">
                          {formatDateTime(act.date)}
                        </span>
                      </div>
                      {act.description && (
                        <p className="bkd__timeline-desc">{act.description}</p>
                      )}
                      <span className="bkd__timeline-user">{act.user}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="bkd__empty">No activity recorded yet.</p>
            )}
          </div>
        )}

        {/* ========= SUBMISSIONS & ACCOUNTS ========= */}
        {activeTab === 'Submissions & Accounts' && (
          <div className="bkd__subs-accts">
            {subsLoading ? (
              <div className="bkd__tab-loading">
                <div className="spinner" />
                <p>Loading submissions and accounts...</p>
              </div>
            ) : (
              <>
                {/* Submissions */}
                <div className="bkd__sa-section">
                  <h3 className="bkd__section-title">Submissions</h3>
                  {subsError ? (
                    <p className="bkd__sa-error">{subsError}</p>
                  ) : brokerSubmissions.length > 0 ? (
                    <div className="bkd__sa-table-wrap card">
                      <table className="bkd__sa-table">
                        <thead>
                          <tr>
                            <th>Insured</th>
                            <th>Status</th>
                            <th>Date</th>
                            <th>Premium</th>
                          </tr>
                        </thead>
                        <tbody>
                          {brokerSubmissions.map((sub) => (
                            <tr
                              key={sub.SubmissionID}
                              className="bkd__sa-row"
                              onClick={() =>
                                navigate(`/submissions/${sub.SubmissionID}`)
                              }
                            >
                              <td className="bkd__sa-name">
                                {sub.InsuredName || '—'}
                              </td>
                              <td>
                                <span
                                  className="bkd__sa-status"
                                  data-status={sub.Status}
                                >
                                  {(sub.Status || 'new').replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="bkd__sa-date">
                                {formatDate(sub.CreatedAt)}
                              </td>
                              <td>
                                {sub.Premium ? formatCurrency(sub.Premium) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="bkd__empty">No submissions found.</p>
                  )}
                </div>

                {/* Accounts */}
                <div className="bkd__sa-section">
                  <h3 className="bkd__section-title">Accounts</h3>
                  {acctsError ? (
                    <p className="bkd__sa-error">{acctsError}</p>
                  ) : brokerAccounts.length > 0 ? (
                    <div className="bkd__sa-table-wrap card">
                      <table className="bkd__sa-table">
                        <thead>
                          <tr>
                            <th>Insured</th>
                            <th>Status</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {brokerAccounts.map((acct) => (
                            <tr
                              key={acct.AccountID}
                              className="bkd__sa-row"
                              onClick={() =>
                                navigate(`/accounts/${acct.AccountID}`)
                              }
                            >
                              <td className="bkd__sa-name">
                                {acct.InsuredName || '—'}
                              </td>
                              <td>
                                <span
                                  className="bkd__sa-status"
                                  data-status={acct.Status}
                                >
                                  {(acct.Status || '—').replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="bkd__sa-date">
                                {formatDate(acct.CreatedAt)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="bkd__empty">No accounts found.</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
